<template>
  <div class="game-screen">
    <header>
      <div class="header-content">
        <div class="player player--one">
          <div class="profile-image"><img :src="playersDetails.homePlayerAvatar" alt=""></div>
          <div class="player__info">
            <div class="player__info__name">{{ playersDetails.homePlayerUsername }}</div>
            <div class="player__info__score">
              <ul>
                <li v-for="(item, index) in numberOfQuestionsPerRound" :key="index" :class="getHomePlayerAnswersHistory(index)"></li>
              </ul>
            </div>
          </div>
        </div>
        <div class="game-timer">
          <div class="game-timer__circle">
            <link href="https://fonts.googleapis.com/css?family=Raleway:400,300,600,800,900" rel="stylesheet" type="text/css">
            <div id="container"></div>
          </div>
          <div class="game-timer__score">
            <span>{{ homePlayerRoundScore }}</span>-<span>{{ awayPlayerRoundScore }}</span>
          </div>
        </div>
        <div class="player player--two">
          <div class="player__info">
            <div class="player__info__name">{{ playersDetails.awayPlayerUsername }}</div>
            <div class="player__info__score">
              <ul>
                <li v-for="(item, index) in numberOfQuestionsPerRound" :key="index" :class="getAwayPlayerAnswersHistory(index)"></li>
              </ul>
            </div>
          </div>
          <div class="profile-image"><img :src="playersDetails.awayPlayerAvatar" alt=""></div>
        </div>
      </div>
    </header>
    <section class="game-main">
      <div class="game-main__category" v-if="!chatEnabled">
        <span :style="getThemeColor"></span>
        <div class="category-icon">
          <img :src="homePlayerCurrentSelectedTheme.iconUrl" alt="">
        </div>
      </div>
      <div class="game-main__category"
           v-if="currentState === states.INITIAL.value && chatEnabled">
        <div class="category-icon"
             style="cursor: pointer"
             @click.prevent="initChat()">
          <img src="static/images/chat/chat_bubble.svg" alt="">
        </div>
      </div>
      <div class="chat_recieved_container"
           v-if="currentState === states.ERROR.value && chatEnabled">
        <h1 class="chat_error">
          {{ getTranslations[states[currentState].text[0]] }} {{ getOpponentUsername() }} {{ getTranslations[states[currentState].text[1]] }}
        </h1>
      </div>
      <div class="chat_recieved_container"
           v-if="currentState === states.RECEIVED.value && chatEnabled">
        <div class="chat_recieved_content">
          <p v-if="currentAnswer.type === answerTypes.EMOJI">
            {{ currentAnswer.sender }} {{ getTranslations[states[currentState].text[0]] }}
            <img :src="getTranslations[currentAnswer.content]" alt="">
          </p>
          <p v-if="currentAnswer.type === answerTypes.TEXT">
            {{ currentAnswer.sender }} {{ getTranslations[states[currentState].text[0]] }}<br>
            {{ getTranslations[currentAnswer.content] }}
          </p>
        </div>
        <div class="chat_action_content">
          <a @click.prevent="sendMessage"
             class="btn_chat_action">
            {{ getTranslations.home_game_chat_reply }}
          </a>
        </div>
      </div>
      <div class="game-q-container">
        <div class="game-main__question">
          <div class="game-main__question__text">
            <p>{{ personalizedData.questionText }}</p>
          </div>
          <div v-if="personalizedData.imageURL" class="game-main__question__image" :style="getQuestionImage"></div>
        </div>
        <div v-if="personalizedData" class="game-main__answers">
          <div v-for="row in (randomizedAnswers.length / 2)" :key="row" class="game-main__answers__row">
            <button v-for="answer in getPartOfAnswers(row)" :key="answer.answerId" @click="registerUserAnswer(answer.answerId, answer.answerKey, true)" :class="getAnswerCSSClasses(answer)" :disabled="getAnswerCSSClasses(answer).toString() === 'btn,rejected'">
              <span>{{ answer.answerText }}</span>
              <div>
                <img v-if="isHomePlayer && homePlayerAnswer === answer.answerKey" class="p1"
                     :src="playersDetails.homePlayerAvatar" alt="">
                <img v-else-if="!isHomePlayer && awayPlayerAnswer === answer.answerKey" class="p2"
                     :src="playersDetails.awayPlayerAvatar" alt="">
                <img v-if="isHomePlayer && bothPlayersAnswered && awayPlayerAnswer === answer.answerKey" class="p2"
                     :src="playersDetails.awayPlayerAvatar" alt="">
                <img v-else-if="!isHomePlayer && bothPlayersAnswered && homePlayerAnswer === answer.answerKey"
                     class="p1" :src="playersDetails.homePlayerAvatar" alt="">
              </div>
            </button>
          </div>
        </div>
        <div class="game-main__bottom">
          <button @click.prevent="useCheatHelp" :disabled="(cheatUsed || displayedAnswerTime === 0 || cheatHelpCounter === 0)" :class="['btn', 'cheat-btn', (cheatUsed || displayedAnswerTime === 0 || cheatHelpCounter === 0) ? 'inactive' : 'active']">{{ getTranslations.home_game_cheat }}<i class="icon-magic"></i>
            <div v-if="cheatHelpCounter > 0" class="jokers_count_outer for_cheat"><div class="jokers_count"><span>{{ cheatHelpCounter }}</span></div></div>
          </button>
          <button @click.prevent="useFiftyFiftyHelp" :disabled="(fiftyFiftyUsed || displayedAnswerTime === 0 || fiftyFiftyHelpCounter === 0)" :class="['btn', 'fifty-btn', (fiftyFiftyUsed || displayedAnswerTime === 0 || fiftyFiftyHelpCounter === 0) ? 'inactive' : 'active']"><i class="icon-dice"></i>50/50
            <div v-if="fiftyFiftyHelpCounter > 0" class="jokers_count_outer for_fifty"><div class="jokers_count"><span>{{ fiftyFiftyHelpCounter }}</span></div></div>
          </button>
        </div>
      </div>
    </section>
    <chat-options v-if="showOptions && chatEnabled" @chose="selectAnswer" @close="showOptions = false"/>
  </div>
</template>

<script>
/* eslint no-console: "off" */
import { mapState, mapGetters, mapMutations } from 'vuex';
import webSocket from '@/common/webSocket';
import { ParseResponseMixin, GameMixin, GameMixin2 } from '@/common/mixins/game';
import ChatMixin from '@/common/mixins/chat/gameChat';
import { Howl } from 'howler';
import shuffle from 'knuth-shuffle-seeded';
import ChatOptions from '@/components/home/modals/game_chat/ChatOptions';

const ProgressBar = require('progressbar.js');

export default {
  name: 'game',
  components: { ChatOptions },
  mixins: [ParseResponseMixin, GameMixin, GameMixin2, ChatMixin],
  data() {
    return {
      personalizedData: null,
      randomizedAnswers: [],
      playerAnswered: false,
      cheatHelpCounter: 0,
      fiftyFiftyHelpCounter: 0,
      playerAnswerID: '',
      playerAnswerKey: '',
      cheatUsed: false,
      isFirstTimeUsingCheatHelp: false,
      fiftyFiftyUsed: false,
      isFirstTimeUsingFiftyFiftyHelp: false,
      currentQuestionID: -1,
      availableAnswers: [],
      bothPlayersAnswered: false,
      questionAnswerTime: 20, // seconds
      secondsElapsed: 0,
      timer: null,
      stompClient: null,
      subscription1: null,
      subscription2: null,
      displayedAnswerTime: null,
      onlyOneCicle: false,
      // TODO implement playing of all sounds
      selectSound: new Howl({
        src: ['static/sounds/Select_Answer.ogg', 'static/sounds/Select_Answer.mp3'],
      }),
      rightAnswerSound: new Howl({
        src: ['static/sounds/right_answer.ogg', 'static/sounds/right_answer.mp3'],
      }),
      wrongAnswerSound: new Howl({
        src: ['static/sounds/wrong_answer.ogg', 'static/sounds/wrong_answer.mp3'],
      }),
      countDownTimerSound: new Howl({
        src: ['static/sounds/CountDownTimer.ogg', 'static/sounds/CountDownTimer.mp3'],
      }),
    };
  },
  created() {
    // console.clear();
    console.log('in Game created hook');
    this.stompClient = webSocket.getGameStompClient();
    console.log('this.stompClient:', this.stompClient);
    this.initQuestionData();
    this.getNextCTA();
    this.getThemesForNextRound();
    if (this.chatEnabled) this.connectToChat();
    this.cheatHelpCounter = this.userDetails.jokersCheat;
    this.fiftyFiftyHelpCounter = this.userDetails.jokersFifty;
    if (this.cheatHelpCounter > 0) this.cheatUsed = false;
    if (this.fiftyFiftyHelpCounter > 0) this.fiftyFiftyUsed = false;
  },
  beforeDestroy() {
    console.log('in game\'s beforeDestroy hook');
    // if for any reason we leave this component earlier from the websocket's response,
    // unsubscribe from the subscribed channels
    this.subscription1.unsubscribe();
    this.subscription2.unsubscribe();
    if (this.chatEnabled) this.disconnectFromChat();
    console.log('clearing timer');
    clearInterval(this.timer);
  },
  methods: {
    ...mapMutations('game', [
      'setHomePlayerAnswer',
      'setAwayPlayerAnswer',
    ]),
    isPvp() {
      return this.playersDetails.homePlayerMSISDN !== this.playersDetails.awayPlayerMSISDN;
    },
    getOpponentUsername() {
      return this.isHomePlayer ? this.playersDetails.awayPlayerUsername : this.playersDetails.homePlayerUsername;
    },
    getNextCTA() {
      console.info(`Subscribing to channel: /topic/ctaSelected/${this.gameID}`);
      this.subscription1 = this.stompClient.subscribe(`/topic/ctaSelected/${this.gameID}`, (response) => {
        console.log('[Game] Inside ctaSelected response');
        // parse server response and return if any errors occur
        const responseBody = this.parseServerResponse(response.body);
        if (responseBody === null) {
          return;
        }
        const responseData = responseBody.data;
        console.log('responseData:', responseData);
        if (responseData.gameStateEnum === 'DISPLAY_CTA_TO_BE_ANSWERED' || responseData.gameStateEnum === 'SHOW_ROUND_ENDED' || responseData.gameStateEnum === 'SHOW_GAME_ROUND_END') {
          this.saveQuestionData(responseData);
          if (this.chatEnabled) this.resetChat();
          clearInterval(this.timer);
        } else if (!this.gameIsResuming && responseData.gameStateEnum === 'CTA_TO_BE_ANSWERED') {
          this.normalTransition = true;
          this.initQuestionData();
        }
      });
    },
    getThemesForNextRound() {
      console.info(`Subscribing to channel: /topic/categoriesFetched/${this.gameID}`);
      this.subscription2 = this.stompClient.subscribe(`/topic/categoriesFetched/${this.gameID}`, (response) => {
        console.log('[Game] Inside categoriesFetched response');
        // parse server response and return if any errors occur
        const responseData = this.parseServerResponse(response.body);
        if (responseData === null) {
          return;
        }
        console.log('responseData:', responseData);
        if (responseData.gameStateEnum === 'CATEGORIES_FETCHED' || responseData.gameStateEnum === 'GAME_FINISHED') {
          this.subscription1.unsubscribe();
          this.subscription2.unsubscribe();
          this.saveRoundData(responseData);
          this.normalTransition = true;
          console.log('showing post round score...');
          this.$router.replace('/roundEnded');
        }
      });
    },
    initQuestionData() {
      console.log('initializing question data...');
      console.log('this.currentQuestionNumber:', this.currentQuestionNumber);
      this.personalizedData = this.isHomePlayer ? this.homePlayerQuestionDTO : this.awayPlayerQuestionDTO;
      if (this.cheatHelpCounter === 0) {
        this.cheatUsed = this.isHomePlayer ? this.homePlayerCheatHelpUsed : this.awayPlayerCheatHelpUsed;
      } else {
        this.cheatUsed = false;
      }
      console.log('cheatUsed: ', this.cheatUsed);
      if (this.fiftyFiftyHelpCounter === 0) {
        this.fiftyFiftyUsed = this.isHomePlayer ? this.homePlayerFiftyFiftyHelpUsed : this.awayPlayerFiftyFiftyHelpUsed;
      } else {
        this.fiftyFiftyUsed = false;
      }
      console.log('fiftyFiftyUsed: ', this.fiftyFiftyUsed);
      console.log('this.personalizedData:', this.personalizedData);
      // first sort answers the same way for both players
      this.randomizedAnswers = this.personalizedData.answerDTOList.slice(0).sort((a, b) => a.answerKey - b.answerKey);
      const seed = (this.gameID * (this.currentQuestionNumber + 1)) / this.currentRoundNumber;
      // using Fisher Yates shuffle algorithm, generate a new shuffled array with the answers
      // but with the same ordering for both players (using a seed)
      shuffle(this.randomizedAnswers, seed);
      console.log('this.currentRoundNumber:', this.currentRoundNumber);
      // init common data
      this.playerAnswered = false;
      this.playerAnswerKey = '';
      this.bothPlayersAnswered = false;
      this.setHomePlayerAnswer('');
      this.setAwayPlayerAnswer('');
      // init help data
      if (this.cheatHelpCounter === 0) {
        this.cheatUsed = this.isHomePlayer ? this.homePlayerCheatHelpUsed : this.awayPlayerCheatHelpUsed;
      } else {
        this.cheatUsed = false;
      }
      console.log('cheatUsed: ', this.cheatUsed);
      if (this.fiftyFiftyHelpCounter === 0) {
        this.fiftyFiftyUsed = this.isHomePlayer ? this.homePlayerFiftyFiftyHelpUsed : this.awayPlayerFiftyFiftyHelpUsed;
      } else {
        this.fiftyFiftyUsed = false;
      }
      console.log('fiftyFiftyUsed: ', this.fiftyFiftyUsed);
      this.isFirstTimeUsingCheatHelp = false;
      this.isFirstTimeUsingFiftyFiftyHelp = false;
      this.availableAnswers = [];
      this.normalTransition = false;
      console.log('calling initAndStartTimer()...');
      this.initAndStartTimer();
    },
    initAndStartTimer() {
      clearInterval(this.timer);
      this.onlyOneCicle = true;
      this.secondsElapsed = 0;
      this.timesForThisRound();
      this.displayedAnswerTime = this.questionAnswerTime;
        /* eslint-disable */
      this.$nextTick(() => {
        this.timer = setInterval(() => {
          if (this.onlyOneCicle) {
            this.onlyOneCicle = false;
            $("#container").html("");
            const bar = new ProgressBar.Circle(container, {
              vm: this,
              color: '#aaa',
              // This has to be the same size as the maximum width to
              // prevent clipping
              strokeWidth: 10,
              trailWidth: 0,
              easing: 'easeInOut',
              duration: this.questionAnswerTime,
              text: {
                autoStyleContainer: false
              },
              from: { color: '#00ac49', width: 10 },
              to: { color: '#E64C3B', width: 10 },
              // Set default step function for all animate calls
              step: function (state, circle) {

                circle.path.setAttribute('stroke', state.color);
                circle.path.setAttribute('stroke-width', state.width);

                var value = Math.round(circle.value() * 100);
//              if (value === 0) {
//                circle.setText(circle._opts.vm.displayedAnswerTime / 1000);
//                circle.path.setAttribute('stroke', '#ff0000');
//              } else if (value < 25) {
//                circle.setText(circle._opts.vm.displayedAnswerTime / 1000);
//                circle.path.setAttribute('stroke', '#0000ff');
//              }
                circle.setText(circle._opts.vm.displayedAnswerTime / 1000);
              }
            }); // var bar
//        <!-- https://github.com/vue-bulma/progress-bar/blob/master/src/ProgressBar.vue-->
//        <!-- http://progressbarjs.readthedocs.io/en/1.0.0/ -->
//        <!-- https://jsfiddle.net/kimmobrunfeldt/72tkyn40/ -->
//          bar.text.style.fontFamily = '"Raleway", Helvetica, sans-serif';
//          bar.text.style.fontSize = '2rem';
            bar.animate(1.0);
          }
          this.secondsElapsed += 1;
          this.displayedAnswerTime -= 1000;
          if ((this.secondsElapsed * 1000)=== this.questionAnswerTime) {
            clearInterval(this.timer);
          }
        }, 1000);
      });
        /* eslint-enable */
    },
    timesForThisRound() {
      this.questionAnswerTime = this.roundTimeList[this.currentRoundNumber - 1];
    },
    getPartOfAnswers(part) {
      if (part === 1) {
        return this.randomizedAnswers.slice(0, this.randomizedAnswers.length / 2);
      }
      return this.randomizedAnswers.slice(this.randomizedAnswers.length / 2);
    },
    registerUserAnswer(answerID, answerKey) {
      console.log('--> inside registerUserAnswer...');
      console.log('this.currentQuestionNumber:', this.currentQuestionNumber);
      console.log('this.playerAnswered:', this.playerAnswered);
      console.log('this.isFirstTimeUsingCheatHelp:', this.isFirstTimeUsingCheatHelp);
      console.log('this.isFirstTimeUsingFiftyFiftyHelp:', this.isFirstTimeUsingFiftyFiftyHelp);
      if (!this.playerAnswered && this.currentQuestionNumber < this.numberOfQuestionsPerRound) {
        if (this.isFirstTimeUsingCheatHelp && !this.availableAnswers.includes(answerID)) {
          return;
        }
        if (this.isFirstTimeUsingFiftyFiftyHelp && !this.availableAnswers.includes(answerID)) {
          return;
        }
        if (this.isHomePlayer) {
          console.log(`user ${this.playersDetails.homePlayerUsername} has answered with answer ${answerKey}...`);
          this.setHomePlayerAnswer(answerKey);
        } else {
          console.log(`user ${this.playersDetails.awayPlayerUsername} has answered with answer ${answerKey}...`);
          this.setAwayPlayerAnswer(answerKey);
        }
        if (this.soundsOn) {
          this.selectSound.play();
        }
        this.playerAnswered = true;
        this.playerAnswerID = answerID;
        this.playerAnswerKey = answerKey;
        // eslint-disable-next-line no-nested-ternary
        clearInterval(this.timer);
        // eslint-disable-next-line
        $("#container").html("");
        console.log('sending player answer');
        this.$axios({
          url: `api/sendSelectedCta2?gameId=${this.gameID}&answer=${this.playerAnswerKey}&questionID=${this.personalizedData.questionId}&useBoost=`,
          method: 'GET',
          headers: {
            Authorization: this.jwtInVuex,
          },
        }).catch((error) => {
          console.log(`Invoking api/sendSelectedCta2 failed with the following error: ${error}`);
        });
      }
    },
    useCheatHelp() {
      if (this.playerAnswered || this.cheatUsed) {
        console.log(`player ${this.userDetails.username} has already used his/her cheat help!`);
        return;
      }
      if (this.soundsOn) {
        this.selectSound.play();
      }
      this.checkHelpAvailabilityAndGetAnswers('CHEAT');
      this.isFirstTimeUsingCheatHelp = true;
      this.cheatUsed = true;
      this.currentQuestionID = this.personalizedData.questionId;
      console.log('this.currentQuestionID', this.currentQuestionID);
    },
    useFiftyFiftyHelp() {
      if (this.cheatUsed && this.currentQuestionID === this.personalizedData.questionId) {
        console.log(`player ${this.userDetails.username} has used his/her cheat help for the same question!`);
        return;
      }
      if (this.playerAnswered || this.fiftyFiftyUsed) {
        console.log(`player ${this.userDetails.username} has already used his/her 50/50 help!`);
        return;
      }
      if (this.soundsOn) {
        this.selectSound.play();
      }
      this.checkHelpAvailabilityAndGetAnswers('FIFTY_FIFTY');
      this.isFirstTimeUsingFiftyFiftyHelp = true;
      this.fiftyFiftyUsed = true;
    },
    checkHelpAvailabilityAndGetAnswers(helpType) {
      const apiCallPart1 = `api/requestCheatForAnswers?msisdn=${this.userDetails.msisdn}&gameID=${this.gameID}&helpType=${helpType}`;
      const apiCallPart2 = `&answer_1_ID=${this.personalizedData.answerDTOList[0].answerId}&answer_2_ID=${this.personalizedData.answerDTOList[1].answerId}`;
      const apiCallPart3 = `&answer_3_ID=${this.personalizedData.answerDTOList[2].answerId}&answer_4_ID=${this.personalizedData.answerDTOList[3].answerId}`;
      console.log(apiCallPart1 + apiCallPart2 + apiCallPart3);
      this.$axios({
        url: apiCallPart1 + apiCallPart2 + apiCallPart3,
        method: 'GET',
        headers: {
          Authorization: this.jwtInVuex,
        },
      }).then((response) => {
        this.availableAnswers = response.data.data.availableAnswers;
        if (helpType === 'FIFTY_FIFTY') {
          this.fiftyFiftyHelpCounter = this.fiftyFiftyHelpCounter - 1;
        }
        if (helpType === 'CHEAT') {
          this.cheatHelpCounter = this.cheatHelpCounter - 1;
        }
        console.log('availableAnswers', this.availableAnswers);
      }).catch((error) => {
        console.log(`Invoking api/requestCheatForAnswers failed with the following error: ${error}`);
      });
    },
    getHomePlayerAnswersHistory(answerIndex) {
      const homePlayerAnswersHistory = this.homePlayerCorrectAnswersCSSClasses[answerIndex];
      return homePlayerAnswersHistory !== undefined ? homePlayerAnswersHistory : '';
    },
    getAwayPlayerAnswersHistory(answerIndex) {
      const awayPlayerAnswersHistory = this.awayPlayerCorrectAnswersCSSClasses[answerIndex];
      return awayPlayerAnswersHistory !== undefined ? awayPlayerAnswersHistory : '';
    },
    getAnswerCSSClasses(answer) {
      const classes = ['btn'];
      if (this.bothPlayersAnswered && (answer.answerId === this.correctAnswerID)) {
        classes.push('correct');
      }
      if ((!this.bothPlayersAnswered && (this.isFirstTimeUsingCheatHelp || this.isFirstTimeUsingFiftyFiftyHelp) && !this.availableAnswers.includes(answer.answerId)) || (this.displayedAnswerTime === 0 && !this.bothPlayersAnswered)) {
        classes.push('rejected');
      }
      return classes;
    },
  },
  computed: {
    ...mapState('profile', [
      'jwtInVuex',
    ]),
    ...mapState('common', [
      'chatEnabled',
      'roundTimeList',
      'userDetails',
      'soundsOn',
    ]),
    ...mapState('game', [
      'numberOfQuestionsPerRound',
      'gameIsResuming',
      'gameID',
      'playersDetails',
      'currentRoundNumber',
      'currentQuestionNumber',
      'correctAnswerID',
      'homePlayerCurrentSelectedTheme',
      'homePlayerRoundScore',
      'homePlayerQuestionDTO',
      'homePlayerCheatHelpUsed',
      'homePlayerFiftyFiftyHelpUsed',
      'homePlayerAnswer',
      'homePlayerCorrectAnswersCSSClasses',
      'awayPlayerRoundScore',
      'awayPlayerQuestionDTO',
      'awayPlayerCheatHelpUsed',
      'awayPlayerFiftyFiftyHelpUsed',
      'awayPlayerAnswer',
      'awayPlayerCorrectAnswersCSSClasses',
    ]),
    ...mapGetters('game', [
      'isHomePlayer',
    ]),
    ...mapGetters('i18n', [
      'getTranslations',
    ]),
    getThemeColor() {
      return {
        borderColor: `rgb(${this.homePlayerCurrentSelectedTheme.red},${this.homePlayerCurrentSelectedTheme.green},${this.homePlayerCurrentSelectedTheme.blue})`,
      };
    },
    getQuestionImage() {
      return {
        backgroundImage: `url(${this.personalizedData.imageURL})`,
      };
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/home/game/Game.vue